import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, TruckIcon, XCircleIcon } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis, Cell, BarChart, Bar } from 'recharts';
const CustomerProfitabilityDashboard: React.FC = () => {
  const kpis = [{
    title: 'Avg Customer Margin',
    value: '15.8%',
    change: '+0.9%',
    isPositive: true,
    subtitle: 'Weighted by revenue'
  }, {
    title: 'Weighted Contribution',
    value: '18.3%',
    change: '+1.2%',
    isPositive: true,
    subtitle: 'Top 10 customers'
  }, {
    title: 'Cost-to-Serve Index',
    value: '108',
    change: '-3 pts',
    isPositive: true,
    subtitle: 'Base 100'
  }];
  const customerPLData = [{
    customer: 'Maruti Suzuki',
    revenue: 1710,
    cogs: 1210,
    grossProfit: 500,
    opex: 240,
    netProfit: 260,
    margin: 15.2
  }, {
    customer: 'Hyundai',
    revenue: 1425,
    cogs: 1015,
    grossProfit: 410,
    opex: 199,
    netProfit: 211,
    margin: 14.8
  }, {
    customer: 'Tata Motors',
    revenue: 1280,
    cogs: 935,
    grossProfit: 345,
    opex: 172,
    netProfit: 173,
    margin: 13.5
  }, {
    customer: 'Mahindra',
    revenue: 980,
    cogs: 695,
    grossProfit: 285,
    opex: 147,
    netProfit: 138,
    margin: 14.1
  }, {
    customer: 'Honda',
    revenue: 850,
    cogs: 590,
    grossProfit: 260,
    opex: 126,
    netProfit: 134,
    margin: 15.8
  }, {
    customer: 'Toyota',
    revenue: 720,
    cogs: 485,
    grossProfit: 235,
    opex: 118,
    netProfit: 117,
    margin: 16.2
  }];
  const bubbleChartData = [{
    customer: 'Maruti Suzuki',
    margin: 15.2,
    risk: 25,
    revenue: 1710,
    color: '#3b82f6'
  }, {
    customer: 'Hyundai',
    margin: 14.8,
    risk: 28,
    revenue: 1425,
    color: '#10b981'
  }, {
    customer: 'Tata Motors',
    margin: 13.5,
    risk: 45,
    revenue: 1280,
    color: '#f59e0b'
  }, {
    customer: 'Mahindra',
    margin: 14.1,
    risk: 32,
    revenue: 980,
    color: '#8b5cf6'
  }, {
    customer: 'Honda',
    margin: 15.8,
    risk: 22,
    revenue: 850,
    color: '#06b6d4'
  }, {
    customer: 'Toyota',
    margin: 16.2,
    risk: 18,
    revenue: 720,
    color: '#ec4899'
  }, {
    customer: 'Kia',
    margin: 14.5,
    risk: 38,
    revenue: 650,
    color: '#f97316'
  }, {
    customer: 'VW Group',
    margin: 13.8,
    risk: 42,
    revenue: 580,
    color: '#84cc16'
  }, {
    customer: 'Renault-Nissan',
    margin: 12.9,
    risk: 68,
    revenue: 520,
    color: '#ef4444'
  }, {
    customer: 'MG Motor',
    margin: 13.2,
    risk: 52,
    revenue: 435,
    color: '#a855f7'
  }];
  const earlyWarnings = [{
    customer: 'Tata Motors',
    type: 'High Returns',
    severity: 'High',
    metric: '4.2% return rate',
    impact: '₹18.5 Cr',
    icon: <XCircleIcon size={16} data-id="element-8313" />,
    color: 'bg-red-100 border-red-300 text-red-800'
  }, {
    customer: 'Renault-Nissan',
    type: 'Payment Delays',
    severity: 'High',
    metric: '68 days avg',
    impact: '₹12.8 Cr outstanding',
    icon: <AlertTriangleIcon size={16} data-id="element-8314" />,
    color: 'bg-red-100 border-red-300 text-red-800'
  }, {
    customer: 'VW Group',
    type: 'Delivery Issues',
    severity: 'Medium',
    metric: '3 late shipments',
    impact: 'Penalty risk',
    icon: <TruckIcon size={16} data-id="element-8315" />,
    color: 'bg-orange-100 border-orange-300 text-orange-800'
  }, {
    customer: 'MG Motor',
    type: 'Quality Claims',
    severity: 'Medium',
    metric: '8 open claims',
    impact: '₹4.2 Cr exposure',
    icon: <AlertTriangleIcon size={16} data-id="element-8316" />,
    color: 'bg-yellow-100 border-yellow-300 text-yellow-800'
  }];
  const profitabilityRanking = [{
    rank: 1,
    customer: 'Toyota',
    revenue: 720,
    margin: 16.2,
    contribution: 117,
    costToServe: 95,
    riskScore: 18,
    trend: 'up'
  }, {
    rank: 2,
    customer: 'Honda',
    revenue: 850,
    margin: 15.8,
    contribution: 134,
    costToServe: 98,
    riskScore: 22,
    trend: 'up'
  }, {
    rank: 3,
    customer: 'Maruti Suzuki',
    revenue: 1710,
    margin: 15.2,
    contribution: 260,
    costToServe: 102,
    riskScore: 25,
    trend: 'stable'
  }, {
    rank: 4,
    customer: 'Hyundai',
    revenue: 1425,
    margin: 14.8,
    contribution: 211,
    costToServe: 105,
    riskScore: 28,
    trend: 'up'
  }, {
    rank: 5,
    customer: 'Kia',
    revenue: 650,
    margin: 14.5,
    contribution: 94,
    costToServe: 108,
    riskScore: 38,
    trend: 'stable'
  }, {
    rank: 6,
    customer: 'Mahindra',
    revenue: 980,
    margin: 14.1,
    contribution: 138,
    costToServe: 110,
    riskScore: 32,
    trend: 'down'
  }, {
    rank: 7,
    customer: 'VW Group',
    revenue: 580,
    margin: 13.8,
    contribution: 80,
    costToServe: 115,
    riskScore: 42,
    trend: 'down'
  }, {
    rank: 8,
    customer: 'Tata Motors',
    revenue: 1280,
    margin: 13.5,
    contribution: 173,
    costToServe: 118,
    riskScore: 45,
    trend: 'down'
  }, {
    rank: 9,
    customer: 'MG Motor',
    revenue: 435,
    margin: 13.2,
    contribution: 57,
    costToServe: 122,
    riskScore: 52,
    trend: 'stable'
  }, {
    rank: 10,
    customer: 'Renault-Nissan',
    revenue: 520,
    margin: 12.9,
    contribution: 67,
    costToServe: 128,
    riskScore: 68,
    trend: 'down'
  }];
  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return <TrendingUpIcon size={14} className="text-green-600" data-id="element-8317" />;
    if (trend === 'down') return <TrendingDownIcon size={14} className="text-red-600" data-id="element-8318" />;
    return <span className="text-gray-400" data-id="element-8319">—</span>;
  };
  const getRiskColor = (score: number) => {
    if (score < 30) return 'text-green-600 bg-green-50';
    if (score < 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };
  return <div data-id="element-8320">
      <DescriptionPanel title="Customer Profitability & Risk Analysis" description="Deep dive into customer-level profitability with P&L analysis, cost-to-serve metrics, and risk indicators. Identify high-value relationships and early warning signals to optimize customer portfolio strategy." insights={['Toyota and Honda leading profitability with 16%+ margins and low risk scores', 'Tata Motors high revenue (₹1,280 Cr) but margin pressure at 13.5% with elevated returns', 'Renault-Nissan showing highest risk score (68) due to payment delays - action required', 'Top 3 customers contribute ₹511 Cr in net profit - 49% of total customer contribution']} data-id="element-8321" />
      {/* KPI Tiles */}
      <div className="mb-8" data-id="element-8322">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-8323">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-8324">
              <div className="flex flex-col h-full" data-id="element-8325">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-8326">
                  {kpi.title}
                </div>
                <div className="text-xs text-gray-400 mb-3" data-id="element-8327">{kpi.subtitle}</div>
                <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-8328">
                  {kpi.value}
                </div>
                <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-8329">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-8330" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-8331" />}
                  {kpi.change} vs last quarter
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Customer P&L Mini-Charts */}
      <div className="mb-8" data-id="element-8332">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-8333">
          Customer P&L Waterfalls
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-8334">
          {customerPLData.map((customer, index) => <Card key={index} className="p-5" data-id="element-8335">
              <div className="mb-4" data-id="element-8336">
                <h3 className="text-sm font-bold text-gray-900" data-id="element-8337">
                  {customer.customer}
                </h3>
                <div className="text-xs text-gray-500" data-id="element-8338">
                  Revenue to Net Profit (₹ Cr)
                </div>
              </div>
              <ResponsiveContainer width="100%" height={180} data-id="element-8339">
                <BarChart data={[{
              name: 'Revenue',
              value: customer.revenue,
              fill: '#3b82f6'
            }, {
              name: 'COGS',
              value: -customer.cogs,
              fill: '#ef4444'
            }, {
              name: 'Gross',
              value: customer.grossProfit,
              fill: '#10b981'
            }, {
              name: 'OpEx',
              value: -customer.opex,
              fill: '#f59e0b'
            }, {
              name: 'Net',
              value: customer.netProfit,
              fill: '#8b5cf6'
            }]} data-id="element-8340">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8341" />
                  <XAxis dataKey="name" tick={{
                fontSize: 10
              }} data-id="element-8342" />
                  <YAxis tick={{
                fontSize: 10
              }} data-id="element-8343" />
                  <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px'
              }} formatter={(value: number) => `₹${Math.abs(value)} Cr`} data-id="element-8344" />
                  <Bar dataKey="value" data-id="element-8345">
                    {[{
                  name: 'Revenue',
                  value: customer.revenue,
                  fill: '#3b82f6'
                }, {
                  name: 'COGS',
                  value: -customer.cogs,
                  fill: '#ef4444'
                }, {
                  name: 'Gross',
                  value: customer.grossProfit,
                  fill: '#10b981'
                }, {
                  name: 'OpEx',
                  value: -customer.opex,
                  fill: '#f59e0b'
                }, {
                  name: 'Net',
                  value: customer.netProfit,
                  fill: '#8b5cf6'
                }].map((entry, i) => <Cell key={`cell-${i}`} fill={entry.fill} data-id="element-8346" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between" data-id="element-8347">
                <span className="text-xs text-gray-600" data-id="element-8348">Net Margin</span>
                <span className="text-sm font-bold text-green-600" data-id="element-8349">
                  {customer.margin}%
                </span>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Bubble Chart and Early Warnings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-8350">
        <Card className="p-6 lg:col-span-2" data-id="element-8351">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-8352">
            Customer Portfolio: Size vs Margin vs Risk
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-8353">
            Bubble size = Revenue | X-axis = Margin % | Y-axis = Risk Score
          </p>
          <ResponsiveContainer width="100%" height={400} data-id="element-8354">
            <ScatterChart data-id="element-8355">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8356" />
              <XAxis type="number" dataKey="margin" name="Margin %" tick={{
              fontSize: 12
            }} label={{
              value: 'Margin %',
              position: 'insideBottom',
              offset: -5,
              fontSize: 12
            }} domain={[12, 17]} data-id="element-8357" />
              <YAxis type="number" dataKey="risk" name="Risk Score" tick={{
              fontSize: 12
            }} label={{
              value: 'Risk Score',
              angle: -90,
              position: 'insideLeft',
              fontSize: 12
            }} domain={[0, 80]} data-id="element-8358" />
              <ZAxis type="number" dataKey="revenue" range={[400, 3000]} name="Revenue" data-id="element-8359" />
              <Tooltip cursor={{
              strokeDasharray: '3 3'
            }} contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: any, name: string) => {
              if (name === 'Revenue') return `₹${value} Cr`;
              if (name === 'Margin %') return `${value}%`;
              return value;
            }} data-id="element-8360" />
              <Legend data-id="element-8361" />
              <Scatter name="Customers" data={bubbleChartData} data-id="element-8362">
                {bubbleChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-8363" />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-8364">
            <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-8365">
              <div className="flex items-center" data-id="element-8366">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-8367" />
                <span className="text-gray-600" data-id="element-8368">Low Risk (&lt;30)</span>
              </div>
              <div className="flex items-center" data-id="element-8369">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2" data-id="element-8370" />
                <span className="text-gray-600" data-id="element-8371">Medium Risk (30-50)</span>
              </div>
              <div className="flex items-center" data-id="element-8372">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2" data-id="element-8373" />
                <span className="text-gray-600" data-id="element-8374">High Risk (&gt;50)</span>
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-6" data-id="element-8375">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8376">
            Early Warning Indicators
          </h3>
          <div className="space-y-3" data-id="element-8377">
            {earlyWarnings.map((warning, index) => <div key={index} className={`${warning.color} border-2 rounded-lg p-3`} data-id="element-8378">
                <div className="flex items-start" data-id="element-8379">
                  <div className="mr-2 mt-0.5" data-id="element-8380">{warning.icon}</div>
                  <div className="flex-1" data-id="element-8381">
                    <div className="flex items-center justify-between mb-1" data-id="element-8382">
                      <span className="text-xs font-bold" data-id="element-8383">
                        {warning.customer}
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 bg-white/50 rounded" data-id="element-8384">
                        {warning.severity}
                      </span>
                    </div>
                    <div className="text-xs font-semibold mb-1" data-id="element-8385">
                      {warning.type}
                    </div>
                    <div className="text-xs" data-id="element-8386">{warning.metric}</div>
                    <div className="text-xs font-medium mt-1" data-id="element-8387">
                      {warning.impact}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-8388">
            <div className="text-xs text-center text-gray-600" data-id="element-8389">
              4 customers flagged for immediate attention
            </div>
          </div>
        </Card>
      </div>
      {/* Profitability Ranking Table */}
      <Card className="p-6" data-id="element-8390">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8391">
          Customer Profitability Ranking
        </h3>
        <div className="overflow-x-auto" data-id="element-8392">
          <table className="w-full" data-id="element-8393">
            <thead data-id="element-8394">
              <tr className="border-b-2 border-gray-300" data-id="element-8395">
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8396">
                  Rank
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8397">
                  Customer
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8398">
                  Revenue
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8399">
                  Margin %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8400">
                  Net Contribution
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8401">
                  Cost-to-Serve
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8402">
                  Risk Score
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8403">
                  Trend
                </th>
              </tr>
            </thead>
            <tbody data-id="element-8404">
              {profitabilityRanking.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-8405">
                  <td className="py-3 px-4 text-center" data-id="element-8406">
                    <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${row.rank <= 3 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' : 'bg-gray-200 text-gray-700'}`} data-id="element-8407">
                      {row.rank}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-8408">
                    {row.customer}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-800" data-id="element-8409">
                    ₹{row.revenue} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-green-600" data-id="element-8410">
                    {row.margin}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-800" data-id="element-8411">
                    ₹{row.contribution} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-8412">
                    {row.costToServe}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-8413">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getRiskColor(row.riskScore)}`} data-id="element-8414">
                      {row.riskScore}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-8415">
                    {getTrendIcon(row.trend)}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-8416">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-8417">
            <div className="text-center" data-id="element-8418">
              <div className="text-gray-600" data-id="element-8419">Total Net Contribution</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-8420">₹1,041 Cr</div>
            </div>
            <div className="text-center" data-id="element-8421">
              <div className="text-gray-600" data-id="element-8422">Avg Margin</div>
              <div className="text-xl font-bold text-green-600" data-id="element-8423">14.5%</div>
            </div>
            <div className="text-center" data-id="element-8424">
              <div className="text-gray-600" data-id="element-8425">Avg Cost-to-Serve</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-8426">110</div>
            </div>
            <div className="text-center" data-id="element-8427">
              <div className="text-gray-600" data-id="element-8428">High Risk Customers</div>
              <div className="text-xl font-bold text-red-600" data-id="element-8429">3</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default CustomerProfitabilityDashboard;